import type { HtsFee } from '@xbcb/api-gateway-client';
import { Checkbox, HiddenField } from '@xbcb/form-item-components';
import { Form } from 'antd';
import MoneyInput from 'components/MoneyInput';
import { useHtsValues } from 'context/htsValues';
import dutyComputation from 'libs/dutyComputation';
import {
  usCbpEntryFeeCodeToTypeMap,
  usCbpEntryTaxCodeToTypeMap,
} from 'libs/taxesFeesBimaps';
import { get } from 'lodash';
import React, { memo } from 'react';
import { useTheme } from 'styled-components';
import { CommercialInvoiceBaseProps } from 'types';
import { StyledLineFeeParagraph } from './styles';

export interface LineItemFeeTabProps extends CommercialInvoiceBaseProps {
  fee: HtsFee;
  index: number;
  lineFee?: string;
  taxClass?: string;
}

/**
 * LineItemFeeTab
 *
 * Fee tab that displays additional fee/tax info for the HTS.
 */

export const LineItemFeeTab: React.FC<LineItemFeeTabProps> = ({
  fullNamePath,
  localNamePath,
  readOnly,
  disabled,
  required,
  fee,
  index,
  lineFee,
  taxClass,
}) => {
  const theme = useTheme();
  const form = Form.useFormInstance();
  const { htsValues } = useHtsValues();
  const { lineHtsNumber, lineHTSObj } = htsValues;

  const fullFeeOptionsNamePath = [...fullNamePath, 'feeOptions', index];
  const feeOptionsNamePath = [...localNamePath, 'feeOptions', index];
  const fullTaxOptionsNamePath = [...fullNamePath, 'taxOptions', index];
  const taxOptionsNamePath = [...localNamePath, 'taxOptions', index];

  const disclaim = Form.useWatch([...fullTaxOptionsNamePath, 'disclaim']);

  const { class: feeClass = '', required: feeRequired, rate, rateCode } = fee;
  const hasLineFee = !!lineFee;
  const hasTaxClass = !!taxClass;
  const isCottonFee = feeClass === '056';
  const isDistilledSpiritOrWineTax = ['016', '017'].includes(feeClass);

  return (
    <>
      {hasLineFee && (
        <>
          <HiddenField
            fullNamePath={[...fullFeeOptionsNamePath, 'required']}
            localNamePath={[...feeOptionsNamePath, 'required']}
            value={true}
            form={form}
          />
          <HiddenField
            fullNamePath={[...fullFeeOptionsNamePath, 'type']}
            localNamePath={[...feeOptionsNamePath, 'type']}
            value={usCbpEntryFeeCodeToTypeMap.get(feeClass)}
            form={form}
          />
        </>
      )}
      {hasTaxClass && (
        <>
          <HiddenField
            fullNamePath={[...fullTaxOptionsNamePath, 'required']}
            localNamePath={[...taxOptionsNamePath, 'required']}
            value={true}
            form={form}
          />
          <HiddenField
            fullNamePath={[...fullTaxOptionsNamePath, 'type']}
            localNamePath={[...taxOptionsNamePath, 'type']}
            value={usCbpEntryTaxCodeToTypeMap.get(feeClass)}
            form={form}
          />
        </>
      )}
      <StyledLineFeeParagraph>
        {hasLineFee ? 'Fee ' : 'Tax '}
        {feeRequired ? 'is required: ' : 'may be required: '}
        {dutyComputation({
          htsNumber: lineHtsNumber,
          duty: rateCode,
          rate: {
            adValorem: rate?.adValorem,
            specific: rate?.specific,
          },
          uom: lineHTSObj?.uom,
        })}
      </StyledLineFeeParagraph>
      {(!feeRequired || isCottonFee) && (
        <Checkbox
          $inline
          $removeSpaceBottom
          form={form}
          readOnly={readOnly}
          disabled={disabled}
          fullNamePath={[
            ...(hasLineFee ? fullFeeOptionsNamePath : fullTaxOptionsNamePath),
            'disclaim',
          ]}
          localNamePath={[
            ...(hasLineFee ? feeOptionsNamePath : taxOptionsNamePath),
            'disclaim',
          ]}
          text="Disclaim?"
        />
      )}
      {!disclaim && hasTaxClass && (
        <Checkbox
          $removeSpaceBottom
          form={form}
          readOnly={readOnly}
          disabled={disabled}
          fullNamePath={[...fullTaxOptionsNamePath, 'defer']}
          localNamePath={[...taxOptionsNamePath, 'defer']}
          text="Defer?"
        />
      )}
      {!disclaim && isDistilledSpiritOrWineTax && (
        <Checkbox
          $removeSpaceBottom
          form={form}
          readOnly={readOnly}
          disabled={disabled}
          fullNamePath={[...fullNamePath, 'productClaimCode']}
          localNamePath={[...localNamePath, 'productClaimCode']}
          text="Craft Beverage Modernization Act Refund"
        />
      )}
      {!disclaim && hasTaxClass && (
        <MoneyInput
          fullNamePath={[...fullTaxOptionsNamePath, 'rate']}
          localNamePath={[...taxOptionsNamePath, 'rate']}
          label={`Tax Rate / ${get(lineHTSObj, ['uom', 0])}`}
          $itemSize={theme.size.tiny}
          max={100}
          precision={6}
          disabled={disabled}
          readOnly={readOnly}
          required={required}
        />
      )}
    </>
  );
};

export default memo(LineItemFeeTab);
